import {
  getQuizAttemptAvailability,
  isAssessmentPassing,
  type ProductTypeAssessmentMode,
  type QuizAttemptAvailability,
  sumAssessmentScore,
} from "./product-assessments";

/**
 * The verdict engine — the single place that turns a product type's assessment
 * configurations (any number of modes) plus the client's answers/quiz state
 * into per-mode statuses and the overall category verdict.
 *
 * AND semantics: the category passes only when every enabled mode passes; it
 * definitively fails as soon as any mode definitively fails (a quiz exhausted
 * without a pass and with no cooldown reset coming, or a completed
 * questionnaire below its pass score); otherwise it is incomplete. A product
 * type with no assessments falls back to the plain yes/no declaration.
 *
 * Consumed by the save-time calculation (API), the flow-completion engine,
 * the flow-run widget (advisor + public) and the PDF renderer. Pure and
 * browser-safe — quiz attempt availability comes from
 * `getQuizAttemptAvailability`, never reimplemented.
 */

export type AssessmentModeStatus = "passed" | "failed" | "incomplete";

/** Which answer decided one mode's outcome. */
export type ProductAssessmentResultSource = "flow" | "external";

/** Overall per-product-type verdict — AND across enabled modes. */
export type ProductAssessmentVerdict = "passed" | "failed" | "incomplete";

/** Minimal quiz state the engine needs (structural subset of ProductAssessmentQuizState). */
export type QuizStateLike = {
  attemptsCount: number;
  lastAttemptAt: string | null;
  /** A pass locks the quiz permanently. */
  passed: boolean;
  attempts?: Array<{ completedAt: string | null; score: number | null }>;
};

/** One mode's config — structural subset of the productTypeAssessment row. */
export type VerdictModeConfig = {
  mode: ProductTypeAssessmentMode;
  minScore: number;
  questions: Array<{ id: string; options: Array<{ id: string; score: number }> }>;
  /** QUIZ only. Null = unlimited. */
  maxAttempts: number | null;
  /** QUIZ only. Null = attempts never reset. */
  attemptResetDays: number | null;
};

export type VerdictModeOutcome = {
  mode: ProductTypeAssessmentMode;
  /** Summed score across the asked questions; null = not yet answered, and always null for an external result. */
  score: number | null;
  minScore: number;
  passed: boolean;
  /** Live tri-state — derived, never persisted (see classifyOutcomeStatus). */
  status: AssessmentModeStatus;
  /** Which answer governed. `"external"` only when nothing was answered in the flow. */
  resultSource: ProductAssessmentResultSource;
  /** The external record that governed, present only when `resultSource` is `"external"`. */
  external?: ExternalResultLike;
};

/**
 * Minimal external record the engine needs (structural subset of
 * ProductAssessmentExternalResult).
 */
export type ExternalResultLike = {
  passed: boolean;
  completedAt: string;
  source: string;
  reference?: string | null;
};

export type ProductTypeVerdict = {
  /** One entry per enabled mode, in config order. */
  outcomes: VerdictModeOutcome[];
  /** Null when no modes are configured — the declaration governs. */
  verdict: ProductAssessmentVerdict | null;
  /** AND of outcomes[].passed; the declaration when no modes are configured. */
  hasExperience: boolean;
};

/**
 * Live quiz status from state + availability. A pass is permanent; a quiz is
 * definitively failed only when no further attempt will ever be possible —
 * attempts exhausted with no cooldown reset coming.
 */
export function classifyQuizStatus(
  state: QuizStateLike | null | undefined,
  availability: QuizAttemptAvailability,
): AssessmentModeStatus {
  if (state?.passed || availability.locked) return "passed";
  if (availability.canAttempt) return "incomplete";
  if (availability.cooldownUntil != null) return "incomplete";
  return "failed";
}

/**
 * Record-based status from a stored outcome — deterministic, no clock. Used
 * where the verdict must not drift between reads (the PDF): an unanswered
 * assessment is incomplete, a completed one is passed or failed.
 */
export function classifyOutcomeStatus(outcome: {
  score: number | null;
  passed: boolean;
}): AssessmentModeStatus {
  if (outcome.passed) return "passed";
  if (outcome.score == null) return "incomplete";
  return "failed";
}

/**
 * AND-combine per-mode statuses into the category verdict: any definitive
 * failure fails the category; all passed passes it; anything else is
 * incomplete. Callers guard non-empty (no modes = declaration governs).
 */
export function combineModeStatuses(statuses: AssessmentModeStatus[]): ProductAssessmentVerdict {
  if (statuses.some((s) => s === "failed")) return "failed";
  if (statuses.every((s) => s === "passed")) return "passed";
  return "incomplete";
}

/** QUIZ score: the last completed attempt's score, or null before any. */
function scoreQuiz(state: QuizStateLike | null | undefined): number | null {
  const lastCompleted = [...(state?.attempts ?? [])].reverse().find((a) => a.completedAt !== null);
  return lastCompleted?.score ?? null;
}

/**
 * QUESTIONNAIRE score: sum of the selected option scores, or null when any
 * question is unanswered (a partially answered questionnaire is not scored).
 */
function scoreQuestionnaire(
  config: VerdictModeConfig,
  answers: Record<string, string> | undefined,
): number | null {
  const selected = answers ?? {};
  const allAnswered = config.questions.every((q) => selected[q.id]);
  if (!allAnswered) return null;
  return sumAssessmentScore(config.questions, selected);
}

/**
 * Has this mode been answered IN THIS FLOW? The whole precedence rule
 * turns on this one question: an external record governs only while the answer
 * is no, and is superseded the instant it becomes yes.
 *
 * QUIZ: a completed attempt, or the permanent pass flag. An attempt merely
 * started (and abandoned) is not an answer, so it does not supersede.
 * QUESTIONNAIRE: every question answered — a partial questionnaire scores null
 * and likewise does not supersede.
 */
function hasFlowAnswer(
  config: VerdictModeConfig,
  quizState: QuizStateLike | null | undefined,
  questionnaireAnswers: Record<string, string> | undefined,
): boolean {
  if (config.mode === "QUIZ") return quizState?.passed === true || scoreQuiz(quizState) !== null;
  return scoreQuestionnaire(config, questionnaireAnswers) !== null;
}

/** Full computation for one product type — per-mode outcomes + overall verdict. */
export function computeProductTypeVerdict(args: {
  configs: VerdictModeConfig[];
  declaredHasExperience: boolean;
  /** This type's questionnaire answers: questionId → selected option id. */
  questionnaireAnswers?: Record<string, string> | undefined;
  /** This type's quiz state (at most one QUIZ mode per type). */
  quizState?: QuizStateLike | null;
  /**
   * This type's externally recorded results, by mode. Consulted ONLY
   * for a mode with no answer in this flow; answering in the flow (pass OR
   * fail) always wins, so a retake overwrites what the external system said.
   */
  externalResults?: Partial<Record<ProductTypeAssessmentMode, ExternalResultLike>> | undefined;
  now: Date;
}): ProductTypeVerdict {
  const outcomes = args.configs.map((config): VerdictModeOutcome => {
    // External precedence, evaluated before either mode's own scoring.
    const external = args.externalResults?.[config.mode];
    if (external && !hasFlowAnswer(config, args.quizState, args.questionnaireAnswers)) {
      return {
        mode: config.mode,
        // The external system scores on its own scale; recording it next to our
        // minScore would invite a false comparison. The outcome is the record.
        score: null,
        minScore: config.minScore,
        passed: external.passed,
        status: external.passed ? "passed" : "failed",
        resultSource: "external",
        external,
      };
    }

    if (config.mode === "QUIZ") {
      const score = scoreQuiz(args.quizState);
      const availability = getQuizAttemptAvailability(
        args.quizState,
        config.maxAttempts,
        config.attemptResetDays,
        args.now,
      );
      const passed = args.quizState?.passed === true;
      return {
        mode: config.mode,
        score,
        minScore: config.minScore,
        passed,
        status: classifyQuizStatus(args.quizState, availability),
        resultSource: "flow",
      };
    }
    const score = scoreQuestionnaire(config, args.questionnaireAnswers);
    const passed = isAssessmentPassing(config.minScore, score);
    return {
      mode: config.mode,
      score,
      minScore: config.minScore,
      passed,
      status: classifyOutcomeStatus({ score, passed }),
      resultSource: "flow",
    };
  });

  if (outcomes.length === 0) {
    return { outcomes, verdict: null, hasExperience: args.declaredHasExperience };
  }
  return {
    outcomes,
    verdict: combineModeStatuses(outcomes.map((o) => o.status)),
    hasExperience: outcomes.every((o) => o.passed),
  };
}
